import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../lib/i18n";
import { pickLocale } from "../../lib/format";
import AdfImage from "./AdfImage";

/**
 * A campaign band, in the reference design — and the reference draws two of
 * them, so `variant` picks between the shapes rather than a second component
 * being written:
 *
 * `perfumery` / `sale` — the photograph fills one half on a beige ground, the
 * copy is centred in the other.
 * `concierge` — no photograph at all: charcoal across the full width with the
 * copy centred on it, which is the treatment the reference gives its dark band.
 *
 * `onCtaClick` turns the call to action into a button, for a band whose next
 * step is a conversation rather than a page. Passing it means `cta_link` is not
 * needed. Scheduling is enforced server-side, so a finished campaign never
 * reaches this component.
 */
export default function CampaignBanner({ block, variant = "sale", testId = "home-campaign", onCtaClick = null }) {
  const { locale } = useI18n();
  if (!block) return null;

  const eyebrow = pickLocale(block.eyebrow, locale);
  const title = pickLocale(block.title, locale);
  const text = pickLocale(block.text, locale);
  const highlight = pickLocale(block.discount_text, locale);
  const cta = pickLocale(block.cta_label, locale);
  if (!title && !block.image_url) return null;

  if (variant === "concierge") {
    return (
      <section className="py-12 md:py-14 bg-white text-charcoal text-center" data-variant={variant} data-testid={testId}>
        <div className="container mx-auto px-6 max-w-3xl">
          {eyebrow && (
            <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-charcoal/55 mb-6">{eyebrow}</p>
          )}
          {title && <h2 className="text-3xl md:text-5xl font-serif mb-6 leading-tight">{title}</h2>}
          {text && (
            <p className="text-charcoal/70 font-medium text-sm mb-10 max-w-lg mx-auto leading-relaxed">{text}</p>
          )}
          {cta && (onCtaClick || block.cta_link) &&
            (onCtaClick ? (
              <button
                type="button"
                onClick={onCtaClick}
                className="border border-charcoal/30 text-charcoal px-10 py-4 text-xs font-semibold uppercase tracking-[0.1em] hover:bg-charcoal hover:text-white transition-colors inline-block rounded-none"
                data-testid={`${testId}-cta`}
              >
                {cta}
              </button>
            ) : (
              <Link
                to={block.cta_link}
                className="border border-charcoal/30 text-charcoal px-10 py-4 text-xs font-semibold uppercase tracking-[0.1em] hover:bg-charcoal hover:text-white transition-colors inline-block rounded-none"
                data-testid={`${testId}-cta`}
              >
                {cta}
              </Link>
            ))}
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-white" data-variant={variant} data-testid={testId}>
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row bg-white">
          <div className="md:w-1/2 aspect-square md:aspect-auto md:min-h-[600px] relative">
            {block.image_url && (
              <AdfImage
                source={block.image_url}
                mobileSource={block.mobile_image_url}
                alt=""
                sizes="(min-width: 768px) 50vw, 100vw"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
          </div>
          <div className="md:w-1/2 flex flex-col justify-center items-center text-center p-12 md:p-24">
            {eyebrow && (
              <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-charcoal/50 mb-4">{eyebrow}</p>
            )}
            {title && (
              <h2 className="text-3xl md:text-5xl font-serif text-charcoal mb-6 tracking-tight">{title}</h2>
            )}
            {highlight && <p className="text-charcoal text-lg font-medium mb-4">{highlight}</p>}
            {text && (
              <p className="text-sm font-medium text-charcoal/60 max-w-sm mb-10 leading-relaxed">{text}</p>
            )}
            {cta && block.cta_link && (
              <Link
                to={block.cta_link}
                className="border-b border-charcoal/30 pb-1 text-xs font-semibold uppercase tracking-[0.1em] hover:border-charcoal transition-colors text-charcoal"
                data-testid={`${testId}-cta`}
              >
                {cta}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
